import interact from "interactjs";
import { cloneDeep, isEqual } from "lodash";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Layout } from "../../models/Layout";
import { ResizeEventPayload } from "../../models/resizeEventPayload";
import { getDynamicItemSize } from "./utils/getDynamicItemSize";
import { getMaxWidthAndHeight } from "./utils/getMaxWidthAndHeight";
import { handleDragMove } from "./utils/handleDragMove";
import { handleResizeMove } from "./utils/handleResizeMove";
import { normalizeLayout } from "./utils/normalizeLayout";
import { resolveOverlaps } from "./utils/resolveOverlaps";

declare class ResizeObserver {
  constructor(callback: ResizeObserverCallback);
  observe(target: Element): void;
  unobserve(target: Element): void;
  disconnect(): void;
}

type ResizeObserverCallback = (
  entries: ResizeObserverEntry[],
  observer: ResizeObserver
) => void;

interface ResizeObserverEntry {
  readonly target: Element;
  readonly contentRect: DOMRectReadOnly;
}

@Component({
  name: "DraggableWrapper",
  components: {},
  computed: {}
})
export default class DraggableWrapper<
  T extends { uuid: string; layout: Layout; propertiesToChart?: T[] }
> extends Vue {
  @Prop({ required: true }) value!: T[];
  @Prop({ default: false }) allowAutoGrow!: boolean;
  @Prop({ default: false }) disabled!: boolean;
  @Prop({ default: () => ({}) }) limits!: { maxX?: number; maxY?: number };
  @Prop({ default: false }) autoExpandOnParentResize!: boolean;

  private readonly MARGIN = -25;
  private isUserInteracting = false;

  internalValue: T[] = [];
  private resizeObserver: ResizeObserver | null = null;
  private lastParentWidth = 0;
  private lastBackgroundSize = { width: 0, height: 0 };

  async mounted() {
    await this.$nextTick();
    await this.$nextTick();
    this.internalValue = this.cloneAndNormalize(this.value);

    await this.waitForParentStabilization();
    await this.fullReconfigure(this.value);

    this.observeParentResize();
  }
  beforeDestroy() {
    this.disconnectResizeObserver();
    this.cleanupInteractions();
  }

  private updateInteractions(newVal: T[], skipInside = false) {
    if (this.isUserInteracting) return;

    let working = cloneDeep(newVal || []);

    if (!skipInside) {
      working = this.ensureInsideParent(working);
    }

    working = resolveOverlaps(working, this.MARGIN);
    working = this.validChildren(working);

    if (!isEqual(this.internalValue, working)) {
      this.internalValue = working;
      this.$nextTick(() => {
        this.applyTransforms();
        this.updateBackgroundSizeAndEmit();
      });
    }
  }

  @Watch("value", { deep: true })
  async onValueChange(newVal: T[]) {
    if (this.isUserInteracting) return;
    await this.fullReconfigure(newVal);
  }

  cleanupInteractions() {
    try {
      const container = this.$el as HTMLElement | null;
      if (!container) return;
      const items = Array.from(
        container.querySelectorAll<HTMLElement>(".dynamic-item")
      );
      items.forEach(el => {
        try {
          interact(el).unset();
        } catch (e) {
          console.warn("Interact unset error", e);
        }
      });
    } catch (e) {
      console.warn("Cleanup interactions error", e);
    }
  }
  private validChildren(list: T[]): T[] {
    if (!list?.length) return list;
    const result = cloneDeep(list);
    result.forEach(parent => {
      let children = parent.propertiesToChart;
      if (!children?.length) return;
      const parentLayout = parent.layout;
      const maxInsideWidth = parentLayout.width - this.MARGIN * 3;

      children = children.map(child => {
        const c = cloneDeep(child);
        const l = c.layout;

        if (l.width > maxInsideWidth) {
          l.width = Math.max(50, maxInsideWidth);
        }
        if (l.x + l.width > maxInsideWidth) {
          l.x = maxInsideWidth - l.width;
        }

        return { ...c, layout: l };
      });

      children = resolveOverlaps(children, this.MARGIN);

      const { height: neededHeight } = getMaxWidthAndHeight(children);
      const requiredHeight = neededHeight + this.MARGIN * 3 + 28;

      if (requiredHeight > parentLayout.height) {
        parentLayout.height = requiredHeight; // expandir verticalmente
      }

      parent.propertiesToChart = children;
    });

    return resolveOverlaps(result, this.MARGIN);
  }

  private async fullReconfigure(newVal: T[]) {
    this.cleanupInteractions();

    let processed = this.cloneAndNormalize(newVal);
    processed = this.ensureInsideParent(processed);
    processed = resolveOverlaps(processed, this.MARGIN);
    processed = this.validChildren(processed);

    this.internalValue = processed;

    await this.$nextTick();
    await this.$nextTick();
    this.applyTransforms();
    this.updateBackgroundSizeAndEmit();
    this.initInteractions();
  }

  private cloneAndNormalize(list: T[]): T[] {
    return cloneDeep(
      list.map(v => ({ ...v, layout: normalizeLayout(v.layout) }))
    );
  }

  private waitForParentStabilization(): Promise<void> {
    return new Promise(resolve => {
      const parent = (this.$el as HTMLElement).parentElement;
      if (!parent) return resolve();

      let lastWidth = parent.getBoundingClientRect().width;
      let stableCount = 0;

      const checkInterval = setInterval(() => {
        parent.offsetHeight;
        const rect = parent.getBoundingClientRect();
        const diff = Math.abs(rect.width - lastWidth);

        if (diff < 0.5) {
          stableCount++;
        } else {
          stableCount = 0;
        }

        lastWidth = rect.width;

        if (stableCount >= 3) {
          clearInterval(checkInterval);
          requestAnimationFrame(() => resolve());
        }
      }, 50);
    });
  }

  emitChange() {
    this.$emit("input", cloneDeep(this.internalValue));
  }

  ensureInsideParent(list: T[]): T[] {
    const parent = this.$el as HTMLElement;
    if (!parent?.parentElement) return list;

    const { width: parentWidth } = getDynamicItemSize(parent.parentElement);
    if (!parentWidth) return list;
    const maxWidth = parentWidth - this.MARGIN * 3;
    return list.map(item => {
      const l = { ...item.layout };

      if (l.width > maxWidth) {
        l.width = Math.max(50, maxWidth);
      }

      if (l.x + l.width > maxWidth) {
        l.x = Math.max(0, maxWidth - l.width);
      }

      l.y = Math.max(0, l.y);
      return { ...item, layout: l };
    });
  }

  private observeParentResize() {
    const parent = this.$el.parentElement;
    if (!parent) return;

    this.disconnectResizeObserver();
    this.resizeObserver = new ResizeObserver(() => {
      if (!this.autoExpandOnParentResize) {
        if (!this.isUserInteracting) {
          this.updateInteractions(this.internalValue);
        }
        return;
      }
      const rect = parent.getBoundingClientRect();
      this.lastParentWidth = rect.width;
      this.updateInteractions(this.value);
    });

    this.resizeObserver.observe(parent);
  }

  private disconnectResizeObserver() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  applyTransforms() {
    if (this.isUserInteracting) return;
    const container = this.$el as HTMLElement | null;
    if (!container) return;

    this.internalValue.forEach(v => {
      const l = v.layout;
      if (!l || !v.uuid) return;
      const el = container.querySelector<HTMLElement>(
        `[data-uuid="${v.uuid}"]`
      );
      if (el) {
        el.style.transform = `translate(${l.x}px, ${l.y}px)`;
        el.style.width = `${l.width}px`;
        el.style.height = `${l.height}px`;
      }
    });
  }

  endAction() {
    this.isUserInteracting = false;

    let resolved = resolveOverlaps(this.internalValue, this.MARGIN);
    resolved = this.validChildren(resolved);
    this.internalValue = resolved;

    this.$nextTick(() => {
      this.applyTransforms();
      this.updateBackgroundSizeAndEmit();
      this.emitChange();
    });
  }
  initInteractions() {
    if (this.disabled) return;
    const container = this.$el as HTMLElement;
    if (!container) return;
    this.internalValue.forEach(item => {
      if (!item || !item.uuid) return;

      const el = container.querySelector<HTMLElement>(
        `[data-uuid="${item.uuid}"]`
      );
      if (!el) return;
      try {
        interact(el).unset();
      } catch (e) {
        console.warn("Interact unset error", e);
      }
      interact(el)
        .draggable({
          allowFrom: ".drag-handle",
          listeners: {
            start: () => {
              el.classList.add("dragging");
              this.isUserInteracting = true;
            },
            move: event => {
              handleDragMove(
                event,
                el,
                item.layout,
                this.MARGIN,
                this.allowAutoGrow,
                this.limits
              );
            },
            end: () => {
              el.classList.remove("dragging");
              this.endAction();
            }
          }
        })
        .resizable({
          edges: { bottom: true, right: true },
          listeners: {
            start: () => {
              el.classList.add("dragging");
              this.isUserInteracting = true;
            },
            move: event => {
              handleResizeMove(
                event,
                el,
                item.layout,
                this.MARGIN,
                this.allowAutoGrow,
                this.limits
              );
            },
            end: () => {
              el.classList.remove("dragging");
              this.endAction();
            }
          }
        });
    });
  }

  @Watch("disabled")
  async onDisabledChange(val: boolean) {
    if (val) {
      this.cleanupInteractions();
      return;
    }
    await this.$nextTick();
    await this.$nextTick();
    this.updateInteractions(this.internalValue);
    this.initInteractions();
    this.applyTransforms();
  }

  private updateBackgroundSizeAndEmit() {
    if (!this.internalValue?.length) return;

    const { width: contentW, height: contentH } = getMaxWidthAndHeight(
      this.internalValue
    );

    const totalHeightNeeded = contentH + this.MARGIN * 3;
    const totalWidthNeeded = contentW + this.MARGIN * 3;

    if (totalHeightNeeded > this.lastBackgroundSize.height) {
      this.$emit("resizeMinimum", {
        width: totalWidthNeeded,
        height: totalHeightNeeded
      });
    }

    this.lastBackgroundSize = {
      width: totalWidthNeeded,
      height: totalHeightNeeded
    };
  }

  get backgroundStyle() {
    if (!this.internalValue?.length) {
      return { width: "100%", height: "100%" };
    }
    const padding = 1;
    const { width, height } = this.lastBackgroundSize;
    return {
      width: `${width + padding}px`,
      height: `${height + padding}px`
    };
  }

  onChildResize(payload: ResizeEventPayload, index: number) {
    if (!payload) return;
    const layout = this.internalValue[index].layout;
    if (payload.height && payload.height > layout.height) {
      this.internalValue[index].layout.height = payload.height;

      this.updateInteractions(this.internalValue);
      this.emitChange();
    }
  }

  getResizeHandler(index: number) {
    return (payload: ResizeEventPayload) => this.onChildResize(payload, index);
  }

  itemStyle({ x, y, width, height }: Layout) {
    return {
      transform: `translate(${x || 0}px, ${y || 0}px)`,
      width: `${width || 250}px`,
      height: `${height || 94}px`
    };
  }
}
